import { z } from "zod";
import { pageSchema } from "@/helpers/types";
import { BaseUserSchema, UserSchema } from "@/types";

// DBT Config item schema (for list view)
export const DBTConfigSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
  description: z.string(),
  user: BaseUserSchema,
  config_schema: z.array(z.unknown()),
  created_on: z.string(),
});

// DBT Config list response schema
export const DBTConfigListResponseSchema = pageSchema(DBTConfigSchema);

// DBT Config filters response schema
export const DBTConfigFiltersResponseSchema = z.object({
  config_names: z.array(z.string()),
  users: z.array(UserSchema),
});

// DBT Config model type patterns schema
export const DBTConfigModelTypePatternsSchema = z.object({
  staging: z.string().optional(),
  mart: z.string().optional(),
  intermediate: z.string().optional(),
  base: z.string().optional(),
});

// DBT Config metadata schema (the configuration metadata object)
export const DBTConfigMetadataSchema = z.object({
  version: z.string(),
  disabled_insights: z.array(z.string()),
  model_type_patterns: DBTConfigModelTypePatternsSchema,
  insights: z.record(z.string(), z.unknown()),
});

// DBT Config detail schema (for single config view with full config)
export const DBTConfigDetailSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
  description: z.string(),
  user: z.object({
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    email: z.string(),
  }),
  config: DBTConfigMetadataSchema,
  config_schema: z.array(z.unknown()),
  created_on: z.string().optional(),
});

// DBT Config check schema (individual check item)
export const DBTConfigCheckSchema = z.object({
  alias: z.string(),
  name: z.string(),
  description: z.string(),
  type: z.string(),
  files_required: z.array(z.string()),
  config: z.object({
    type: z.string().optional(),
    required: z.array(z.string()).optional(),
    properties: z.record(z.string(), z.unknown()),
  }),
});

// DBT Config checks response schema
export const DBTConfigChecksResponseSchema = z.object({
  checks: z.array(DBTConfigCheckSchema),
});

// DBT Config checks filters response schema
export const DBTConfigChecksFiltersResponseSchema = z.object({
  check_types: z.array(z.string()),
  files_required: z.array(z.string()),
});

// Standard response schema for create/update operations
export const DBTConfigMutationResponseSchema = z.object({
  ok: z.boolean(),
});

// DBT Config request schema (for create/update)
export const DBTConfigRequestSchema = z.object({
  name: z.string(),
  description: z.string(),
  config: DBTConfigMetadataSchema,
  config_schema: z.array(z.unknown()),
});

// Inferred types
export type DBTConfig = z.infer<typeof DBTConfigSchema>;
export type DBTConfigListResponse = z.infer<typeof DBTConfigListResponseSchema>;
export type DBTConfigFiltersResponse = z.infer<typeof DBTConfigFiltersResponseSchema>;
export type DBTConfigDetail = z.infer<typeof DBTConfigDetailSchema>;
export type DBTConfigCheck = z.infer<typeof DBTConfigCheckSchema>;
export type DBTConfigChecksResponse = z.infer<typeof DBTConfigChecksResponseSchema>;
export type DBTConfigChecksFiltersResponse = z.infer<typeof DBTConfigChecksFiltersResponseSchema>;
export type DBTConfigMutationResponse = z.infer<typeof DBTConfigMutationResponseSchema>;
export type DBTConfigRequest = z.infer<typeof DBTConfigRequestSchema>;
export type DBTConfigModelTypePatterns = z.infer<typeof DBTConfigModelTypePatternsSchema>;
export type DBTConfigMetadata = z.infer<typeof DBTConfigMetadataSchema>;
